<template>
  <VueDatePicker
    v-input-aria="inputAriaAttrs"
    :key="dateFormat"
    v-model="dateValue"
    :inline="inline"
    :range="range"
    :disabled="disabled"
    :teleport="teleportTarget"
    :formats="dateFormats"
    :time-config="{ enableTimePicker: false }"
    :config="{ monthChangeOnArrows: false }"
    :ui="{
      input: `${inputFocusClass} ${$slots.leading ? 'pl-8!' : ''} ${$slots.trailing ? 'pe-[calc(var(--p-input-date-trailing-width)+1rem)]!' : ''} ${props.inputClass} leading-none! border-none! surface! ring! ring-inset! ring-gray-lighten-1/50! dark:ring-gray-lighten-1-dark/50! placeholder:opacity-100! sm:text-sm! disabled:dark:bg-surface/5! disabled:cursor-not-allowed! disabled:bg-gray-50! disabled:text-gray-400!`,
      menu: '-mt-2.5 [&_:focus-visible]:outline-secondary',
      dayClass: props.dayClass
    }"
    :locale="nl"
    :week-numbers="{ type: 'iso', label: 'w' }"
    :action-row="{
      selectBtnLabel: 'OK',
      cancelBtnLabel: 'Annuleer'
    }"
    auto-apply
    :dark="isDarkMode"
    ref="datepickerElem"
    :input-attrs="inputAttrs"
    :style="{ '--p-input-date-trailing-width': `${trailingWidth}px` }"
    @open="onDatepickerModalOpen"
    @closed="onDatepickerModalClosed"
    @focus="emitFormFocus"
    @blur="emitFormBlur"
    @keydown.left.right.up.down.capture="onArrowKeysDown"
    @keydown.enter.capture="onEnterKeyDown"
    @keydown.esc.capture="close"
    @dragstart.prevent
    class="select-none"
    @update-month-year="lastMonthYear = $event"
  >
    <template v-if="$slots.leading" #input-icon>
      <span
        class="text-on-surface dark:text-on-surface-dark ml-2"
        :class="disabled ? 'text-gray-400' : ''"
      >
        <slot name="leading" />
      </span>
    </template>
    <template v-if="$slots.trigger" #trigger>
      <slot name="trigger" />
    </template>
    <template #clear-icon="{ clear }">
      <span ref="trailingElem" class="mx-2 inline-flex items-center gap-2" @click.stop>
        <slot name="trailing" :clear="clear" />
        <FontAwesomeIcon
          v-if="canClear"
          :icon="['fas', 'xmark']"
          size="sm"
          @click.stop.prevent="clear"
        />
      </span>
    </template>
  </VueDatePicker>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useAttrs, useSlots, useTemplateRef, watch } from "vue"
import moment, { type Moment } from "moment"
import type { InputAttributesConfig } from "@vuepic/vue-datepicker"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { onClickOutside, useElementSize, useEventListener } from "@vueuse/core"
import { useDeviceDetection } from "@/composables/utils/device-detection.ts"
import { nl } from "date-fns/locale"
import { useUserSettingsStore } from "@/store/user-settings.store.ts"
import { storeToRefs } from "pinia"
import type { DateGuard } from "@/components/form/date-guards/date-guard.ts"
import { useFormField } from "@nuxt/ui/composables/useFormField"
import type { DirectiveBinding } from "vue"

const inputFocusClass =
  "focus:outline-none! focus-visible:outline-none! focus:ring-2! focus:ring-inset! focus:ring-secondary! dark:focus:ring-secondary-dark! focus-visible:ring-2! focus-visible:ring-inset! focus-visible:ring-secondary! dark:focus-visible:ring-secondary-dark!"

const attrs = useAttrs()
const slots = useSlots()
const trailingElem = useTemplateRef<HTMLElement>("trailingElem")
const { width: trailingWidth } = useElementSize(trailingElem)
const configuredInputAttrs = computed(
  () => (attrs["input-attrs"] ?? attrs.inputAttrs ?? {}) as Partial<InputAttributesConfig>
)
const canClear = computed(
  () =>
    (Array.isArray(modelValue.value) ? modelValue.value.length > 0 : !!modelValue.value) &&
    inputAttrs.value.clearable !== false &&
    attrs.clearable !== false &&
    !disabled.value &&
    attrs.readonly !== "" &&
    !attrs.readonly
)

const modelValue = defineModel<Moment | Moment[] | null>({ required: true })

const props = withDefaults(
  defineProps<{
    id?: string
    name?: string
    disabled?: boolean
    required?: boolean
    inline?: boolean
    teleport?: boolean | string
    range?: boolean
    /**
     * Optional date guard(s) to validate/confirm date changes before applying them.
     * Each guard is run sequentially. If all guards call `next()`, the model value is updated.
     * This allows for multiple chained "are you sure" prompts.
     */
    dateGuards?: DateGuard | DateGuard[]
    inputClass?: string
    dayClass?: (date: Date, internalModelValue: Date | Date[] | null) => string
  }>(),
  {
    disabled: undefined,
    required: undefined,
    inputClass: "",
    inline: false,
    teleport: true,
    range: false
  }
)

const emit = defineEmits(["update:isBeingPrompted", "close", "isPickingDate"])

const {
  id,
  name,
  disabled,
  labelId,
  ariaAttrs,
  emitFormFocus,
  emitFormBlur,
  emitFormInput,
  emitFormChange
} = useFormField({
  get id() {
    return props.id ?? configuredInputAttrs.value.id
  },
  get name() {
    return props.name ?? configuredInputAttrs.value.name
  },
  get disabled() {
    return props.disabled
  }
})

const inputAriaAttrs = computed(() => ({
  "aria-invalid":
    attrs["aria-invalid"] ??
    (configuredInputAttrs.value.state === false ? true : ariaAttrs.value?.["aria-invalid"]),
  "aria-labelledby": attrs["aria-labelledby"] ?? labelId.value,
  "aria-describedby":
    [ariaAttrs.value?.["aria-describedby"], attrs["aria-describedby"]].filter(Boolean).join(" ") ||
    undefined
}))

const inputAttrs = computed<Partial<InputAttributesConfig>>(() => ({
  ...configuredInputAttrs.value,
  id: id.value,
  name: name.value,
  required: props.required ?? configuredInputAttrs.value.required,
  state:
    inputAriaAttrs.value["aria-invalid"] === true || inputAriaAttrs.value["aria-invalid"] === "true"
      ? false
      : configuredInputAttrs.value.state,
  clearable:
    attrs.clearable === undefined
      ? (configuredInputAttrs.value.clearable ?? true)
      : attrs.clearable !== false,
  hideInputIcon: !slots.leading,
  alwaysClearable: !!slots.trailing || configuredInputAttrs.value.alwaysClearable
}))

// VueDatePicker only forwards a fixed set of inputAttrs; apply ARIA metadata to its real input.
function applyInputAria(
  element: HTMLElement,
  { value }: DirectiveBinding<typeof inputAriaAttrs.value>
) {
  const input = element.querySelector('input[data-test-id="dp-input"]')
  for (const [attribute, content] of Object.entries(value)) {
    if (content === undefined) input?.removeAttribute(attribute)
    else input?.setAttribute(attribute, String(content))
  }
}
const vInputAria = { mounted: applyInputAria, updated: applyInputAria }

const userSettingsStore = useUserSettingsStore()
const { isDarkMode } = storeToRefs(userSettingsStore)

function focus() {
  if (datepickerElem.value && datepickerElem.value.$el.getElementsByTagName("input")[0]) {
    datepickerElem.value.$el.getElementsByTagName("input")[0].focus()
  }
}

function openMenu() {
  datepickerElem.value.openMenu()
}

defineExpose({
  focus,
  openMenu
})

/**
 * Whether the user is currently picking a date. I.e. modal or confirm dialog is open.
 */
const datepickerModalIsOpen = ref(false)
/**
 * The date that is currently focused in the menu.
 */
let menuFocusedDate: Moment | null = null
let menuMonthYear: { month: number; year: number } | null = null

const guards = computed(() => {
  if (!props.dateGuards) return []
  return Array.isArray(props.dateGuards) ? props.dateGuards : [props.dateGuards]
})

const isBeingPrompted = computed(() => guards.value.some((g) => g.isBeingPrompted.value))

/**
 * Runs all date guards sequentially. Each guard must call `next()` to proceed.
 * If all guards pass, the model value is updated.
 * The next guard is deferred via nextTick so that any dialog cleanup (e.g. currentDialog = null)
 * completes before the next guard potentially shows a new dialog.
 */
function affirmDate(val: Moment | Moment[] | null) {
  function runGuard(index: number) {
    if (index >= guards.value.length) {
      modelValue.value = val
      emitFormInput()
      emitFormChange()
      return
    }
    guards.value[index].run(val, () => nextTick(() => runGuard(index + 1)))
  }

  runGuard(0)
}

watch(isBeingPrompted, (val: boolean) => emit("update:isBeingPrompted", val))

const datepickerElem = ref()

const dateValue = computed({
  get() {
    if (Array.isArray(modelValue.value)) {
      return modelValue.value.map((date) => date?.toDate())
    }
    return modelValue.value?.toDate()
  },
  set(value: Date | Date[] | null) {
    if (value) {
      if (Array.isArray(value)) {
        const dates = value
          .filter((date) => date != null)
          .map((date) => {
            const timezoneOffsetInMilliseconds = date.getTimezoneOffset() * 60 * 1000
            const adjustedDate = new Date(date.getTime() - timezoneOffsetInMilliseconds)
            const dateString = adjustedDate.toISOString().substring(0, 10) // Only keep the date portion of the ISO string.
            return moment(dateString, "YYYY-MM-DD")
          })
        affirmDate(dates.length > 0 ? dates : null)
      } else {
        const timezoneOffsetInMilliseconds = value.getTimezoneOffset() * 60 * 1000
        const adjustedDate = new Date(value.getTime() - timezoneOffsetInMilliseconds)
        const dateString = adjustedDate.toISOString().substring(0, 10) // Only keep the date portion of the ISO string.
        const mom = moment(dateString, "YYYY-MM-DD")
        affirmDate(mom)
      }
    } else {
      affirmDate(null)
    }
  }
})

const datepickerElement = computed(() => datepickerElem.value?.$el as HTMLElement | undefined)
const dialogTarget = ref<string>()
onMounted(() => {
  const dialog = datepickerElement.value?.closest<HTMLElement>(
    '[data-datepicker-portal], [role="dialog"]'
  )
  if (dialog?.id) dialogTarget.value = `#${CSS.escape(dialog.id)}`
})
const teleportTarget = computed(() =>
  props.inline ? false : props.teleport === true ? (dialogTarget.value ?? "body") : props.teleport
)
const datepickerMenuElement = computed(
  () => datepickerElem.value?.dpWrapMenuRef?.().value as HTMLElement | null | undefined
)
const { isMediumMq, isMobileContainerMq } = useDeviceDetection(datepickerElement)

const dateFormat = computed(() =>
  isMediumMq.value || isMobileContainerMq.value ? "DD-MM-YY" : "DD-MM-YY [wk] w"
)
const dateFormats = computed(() => ({
  input: (date: Date | Date[]) => formatDate(date, dateFormat.value)
}))

function formatDate(date: Date | Date[], format: string) {
  const today = moment()

  if (Array.isArray(date)) {
    // Handle date ranges
    if (date.length === 2) {
      const startDate = moment(date[0])
      const endDate = moment(date[1])

      const startDateFormatted = startDate.format("DD-MM-YYYY [wk] W")
      const endDateFormatted = endDate.format("DD-MM-YYYY [wk] W")

      return `${startDateFormatted} t/m ${endDateFormatted}`
    } else {
      // Other cases
      return date
        .map((d) => {
          if (moment(d).isSame(today, "day")) {
            return "Vandaag"
          } else {
            return moment(d).format(format)
          }
        })
        .join(", ")
    }
  } else {
    if (moment(date).isSame(today, "day")) {
      return "Vandaag"
    } else {
      return moment(date).format(format)
    }
  }
}

useEventListener(
  document,
  "keydown",
  (e) => {
    if (props.inline || !datepickerModalIsOpen.value || !isEventInDatepicker(e)) {
      return
    }

    if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) {
      onArrowKeysDown(e)
    } else if (e.key === "Enter") {
      onEnterKeyDown(e)
    } else if (e.key === "Escape") {
      closeMenu(e)
    }
  },
  { capture: true }
)

onClickOutside(datepickerElem, () => {
  if (datepickerModalIsOpen.value) {
    close()
  }
})

function close(e?: Event) {
  if (e instanceof KeyboardEvent) {
    if (!props.inline && !datepickerModalIsOpen.value) {
      return
    }

    e.preventDefault()
    e.stopPropagation()
  }

  emit("close")
  if (!datepickerModalIsOpen.value) {
    emit("isPickingDate", false)
  }
}

function closeMenu(e?: KeyboardEvent) {
  e?.preventDefault()
  e?.stopPropagation()

  if (datepickerElem.value?.closeMenu) {
    datepickerElem.value.closeMenu()
    return
  }

  close()
}

function onDatepickerModalOpen() {
  datepickerModalIsOpen.value = true
  lastMonthYear.value = null
  emit("isPickingDate", true)
}

function onDatepickerModalClosed() {
  datepickerModalIsOpen.value = false
  emit("close")
  if (!isBeingPrompted.value) {
    emit("isPickingDate", false)
  }
}

function onArrowKeysDown(e: KeyboardEvent) {
  if (e.target instanceof Node && trailingElem.value?.contains(e.target)) return

  if (!datepickerModalIsOpen.value) {
    return
  }

  e.preventDefault()
  e.stopPropagation()

  /* Set initial focused date value (selected date or today) */
  if (!menuFocusedDate) {
    if (Array.isArray(modelValue.value) && modelValue.value.length > 0 && modelValue.value[0]) {
      menuFocusedDate = modelValue.value[0].clone()
    } else if (modelValue.value && !Array.isArray(modelValue.value)) {
      menuFocusedDate = modelValue.value.clone()
    } else {
      menuFocusedDate = getTodayDateMoment()
    }
  }

  /* Map arrow keys to date increments/decrements */
  if (menuFocusedDate) {
    if (e.key === "ArrowRight") {
      menuFocusedDate = menuFocusedDate.clone().add(1, "day")
    } else if (e.key === "ArrowLeft") {
      menuFocusedDate = menuFocusedDate.clone().subtract(1, "day")
    } else if (e.key === "ArrowUp") {
      menuFocusedDate = menuFocusedDate.clone().subtract(7, "day")
    } else if (e.key === "ArrowDown") {
      menuFocusedDate = menuFocusedDate.clone().add(7, "day")
    }
  }

  /* Try to focus the new date cell. If it doesn't exist, change the month and try again. */
  let calendarCell = getCalendarCellElem(menuFocusedDate)

  if (calendarCell) {
    calendarCell.focus()
  } else {
    changeMenuMonth(["ArrowUp", "ArrowLeft"].includes(e.key) ? -1 : 1)
    setTimeout(() => {
      calendarCell = getCalendarCellElem(menuFocusedDate)
      if (calendarCell) {
        calendarCell.focus()
      }
    })
  }
}

function onEnterKeyDown(e: KeyboardEvent) {
  if (e.target instanceof Node && trailingElem.value?.contains(e.target)) return

  /* If the datepicker is not open, do nothing. */
  if (!datepickerModalIsOpen.value) {
    return
  }

  e.preventDefault()
  e.stopPropagation()

  if (props.range) {
    // Use the calendar's range selection so pending dates and validation stay in sync.
    const firstDate = Array.isArray(modelValue.value) ? modelValue.value[0] : modelValue.value
    const date = menuFocusedDate ?? firstDate ?? getTodayDateMoment()
    const focusedCell =
      e.target instanceof HTMLElement ? e.target.closest<HTMLElement>('[role="gridcell"]') : null
    const cell = focusedCell ?? getCalendarCellElem(date)
    cell?.click()
    return
  }

  // If no date is focused, set the today's date.
  if (!menuFocusedDate) {
    if (!modelValue.value) {
      modelValue.value = getTodayDateMoment()
    }
    datepickerElem.value.closeMenu()
  } else {
    // Check if date is a range.
    if (Array.isArray(modelValue.value)) {
      modelValue.value.push(menuFocusedDate.clone())
      // Close if range is complete.
      if (modelValue.value.length === 2) {
        datepickerElem.value.closeMenu()
      }
    } else {
      modelValue.value = menuFocusedDate.clone()
      datepickerElem.value.closeMenu()
    }
  }
}

function getCalendarCellElem(date: Moment | null) {
  if (!date) return null
  const cellSelector = `[id="dp-${date.format("YYYY-MM-DD")}"]`
  const menuElement = props.inline ? datepickerElement.value : datepickerMenuElement.value
  return menuElement?.querySelector<HTMLElement>(cellSelector) ?? null
}

function isEventInDatepicker(e: KeyboardEvent) {
  const target = e.target

  return (
    target instanceof Node &&
    (Boolean(datepickerElement.value?.contains(target)) ||
      Boolean(datepickerMenuElement.value?.contains(target)))
  )
}

const lastMonthYear = ref<{ month: number; year: number } | null>(null)

// Changes the month of the menu, used by arrow keys
function changeMenuMonth(offsetFromCurrent: number) {
  let newMonth: number
  let newYear: number
  if (Array.isArray(modelValue.value)) {
    const firstDate = modelValue.value[0] || moment()
    newMonth = firstDate.month() + offsetFromCurrent
    newYear = firstDate.year()
  } else {
    newMonth = (modelValue.value?.month() ?? moment().month()) + offsetFromCurrent
    newYear = modelValue.value?.year() ?? moment().year()
  }
  if (lastMonthYear.value) {
    newMonth = lastMonthYear.value.month + offsetFromCurrent
    newYear = lastMonthYear.value.year
  }

  if (newMonth < 0) {
    newMonth = 11
    newYear -= 1
  } else if (newMonth > 11) {
    newMonth = 0
    newYear += 1
  }
  menuMonthYear = {
    month: newMonth,
    year: newYear
  }

  datepickerElem.value.setMonthYear(menuMonthYear)
}

function getTodayDateMoment() {
  /* Important to have it instantiated as a date, and not datetime. */
  return moment(moment().format("YYYY-MM-DD"), "YYYY-MM-DD")
}

watch(datepickerModalIsOpen, (val) => {
  if (val) {
    if (Array.isArray(modelValue.value)) {
      const firstDate = modelValue.value[0] || moment()
      menuMonthYear = {
        month: firstDate.month(),
        year: firstDate.year()
      }
    } else {
      menuMonthYear = {
        month: modelValue.value?.month() ?? moment().month(),
        year: modelValue.value?.year() ?? moment().year()
      }
    }
  } else {
    setTimeout(() => {
      menuFocusedDate = null
    })
  }
})
</script>

<style lang="scss" scoped>
.dp--main:deep(.dp--arrow-top) {
  display: none;
}
</style>
